/**
 * Emergency Participant Dashboard
 * 
 * Real-time dashboard showing active participants with adaptive retry visualization
 */

import React, { useEffect } from 'react';
import { Badge, Card, Group, Stack, Text, Title, Alert } from '@mantine/core';
import { IconAlertTriangle, IconUsers, IconRefresh } from '@tabler/icons-react';
import { useEmergencyParticipant, useEmergencyLevel, useRecommendedRetryCount } from '../hooks/useEmergencyParticipant';

export interface EmergencyParticipantDashboardProps {
  emergencyId: string;
  emergencyName: string;
  currentUserId: string;
  autoRegister?: boolean;
}

/**
 * Dashboard component for emergency participant tracking
 * 
 * Features:
 * - Real-time participant count (refreshes every 5s)
 * - Automatic emergency level classification
 * - Adaptive retry count recommendations
 * - One-click participant registration
 */
export function EmergencyParticipantDashboard({
  emergencyId,
  emergencyName,
  currentUserId,
  autoRegister = false,
}: EmergencyParticipantDashboardProps) {
  const {
    participantCount,
    loading,
    error,
    registerParticipant,
    unregisterParticipant,
    refetchCount,
  } = useEmergencyParticipant(emergencyId);

  const emergencyLevel = useEmergencyLevel(participantCount);
  const recommendedRetries = useRecommendedRetryCount(participantCount);

  // Auto-register on mount if enabled
  useEffect(() => {
    if (autoRegister && !loading && currentUserId) {
      registerParticipant(emergencyId, currentUserId).catch(console.error);
    }
  }, [autoRegister, currentUserId, emergencyId, loading, registerParticipant]);

  // Get emergency level color
  const getLevelColor = (level: string): string => {
    switch (level) {
      case 'P1':
        return 'red';
      case 'P2':
        return 'orange';
      case 'P3':
        return 'yellow';
      case 'P4':
        return 'blue';
      default:
        return 'gray';
    }
  };

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={3}>{emergencyName}</Title>
          <Badge color={getLevelColor(emergencyLevel)} size="lg">
            {emergencyLevel}
          </Badge>
        </Group>

        {error && (
          <Alert
            icon={<IconAlertTriangle size={16} />}
            title="Error"
            color="red"
            variant="filled"
          >
            {error.message}
          </Alert>
        )}

        <Card withBorder p="sm" bg="gray.0">
          <Group justify="space-between">
            <Group gap="xs">
              <IconUsers size={24} />
              <div>
                <Text size="sm" c="dimmed">
                  Active Participants
                </Text>
                <Text size="xl" fw={700}>
                  {loading ? '...' : participantCount.toLocaleString()}
                </Text>
              </div>
            </Group>

            <div style={{ textAlign: 'right' }}>
              <Text size="sm" c="dimmed">
                Emergency Level
              </Text>
              <Text size="lg" fw={600} c={getLevelColor(emergencyLevel)}>
                {emergencyLevel}
              </Text>
            </div>
          </Group>
        </Card>

        <Card withBorder p="sm" bg="blue.0">
          <Group justify="space-between">
            <div>
              <Text size="sm" c="dimmed">
                Adaptive Retry Count
              </Text>
              <Text size="lg" fw={600}>
                {recommendedRetries} retries
              </Text>
            </div>

            <Group gap="xs">
              <IconRefresh size={20} />
              <Text size="xs" c="dimmed">
                {participantCount < 1000
                  ? 'Standard (3 base)'
                  : participantCount < 10000
                  ? 'Small Scale (+4)'
                  : participantCount < 50000
                  ? 'Medium Scale (+7)'
                  : 'Large Scale (+12)'}
              </Text>
            </Group>
          </Group>
        </Card>

        <Stack gap="xs">
          <Text size="sm" fw={500}>
            Retry Strategy
          </Text>
          <Text size="xs" c="dimmed">
            • Base retries: 3 (standard write)
          </Text>
          <Text size="xs" c="dimmed">
            • Life-critical boost: +5 retries
          </Text>
          {participantCount > 1000 && (
            <Text size="xs" c="dimmed">
              • Scale adjustment: +
              {participantCount > 50000
                ? 12
                : participantCount > 10000
                ? 7
                : 4}{' '}
              retries ({participantCount.toLocaleString()} participants)
            </Text>
          )}
          {emergencyLevel === 'P1' && (
            <Text size="xs" c="dimmed">
              • P1 emergency boost: +5 retries
            </Text>
          )}
          <Text size="xs" fw={600} c="blue">
            Total: {recommendedRetries} retries @ 50ms delay
          </Text>
        </Stack>

        <Text size="xs" c="dimmed" fs="italic">
          System automatically adjusts retry behavior based on participant count and emergency level.
          More participants = more retries = higher reliability during scale.
        </Text>
      </Stack>
    </Card>
  );
}

